import { supabase } from '../supabaseClient';
import { filterVisibleItems } from '../middleware/permissions';
import { getCachedUserAndProfile } from '../utils/userCache';
import { logAudit } from './auditLog';
import { updateTicketActivity } from './ticketActivity';

/**
 * 建立新 Item
 */
export const createItem = async (data) => {
  try {
    const insertData = {
      folder_id: data.folder_id,
      title: data.title,
      description: data.description || null,
      start_date: data.start_date || null,
      end_date: data.end_date || null,
      days: data.days || 1,
      status: data.status || 'pending',
      // 如果 status 是 completed，completed 必須是 true
      completed: data.status === 'completed' ? true : (data.completed || false),
      priority: data.priority || 0,
      creator_id: data.creator_id,
      assignee_id: data.assignee_id || null,
      department_id: data.department_id || null,
      company_id: data.company_id || null,
      display_order: data.display_order || 0,
      // 如果一開始就是 completed，設定 completed_at
      completed_at: (data.status === 'completed' || data.completed) 
        ? new Date().toISOString() 
        : null,
    };
    
    const { data: item, error } = await supabase
      .from('items')
      .insert(insertData)
      .select('*')
      .single();

    if (error) throw error;

    await logAudit('create', 'item', item.id, item.title, {
      after: item
    });

    return { data: item, error: null };
  } catch (error) {
    console.error('Error creating item:', error);
    return { data: null, error };
  }
};

/**
 * 批次取得多個 Folders 下的所有 Items（包含子資料夾）
 * @param {number[]} folderIds - 資料夾 ID 陣列
 * @param {object} cachedProfile - (選用) 已快取的 profile
 * @returns {Promise<{data: Object, error: any}>} - 返回 { folderId: items[] } 的 map
 */
export const getBatchItemsByFolders = async (folderIds, cachedProfile = null) => {
  try {
    // 如果沒有 folder，直接返回
    if (!folderIds || folderIds.length === 0) {
      return { data: {}, error: null };
    }
    
    // 取得 profile（優先使用傳入的，否則使用快取）
    let currentProfile = cachedProfile;
    if (!currentProfile) {
      const { profile } = await getCachedUserAndProfile(supabase);
      if (!profile) return { data: {}, error: new Error('找不到使用者資料') };
      currentProfile = profile;
    }
    
    // ⚡ 使用新的批次查詢 RPC，一次取得所有 folders 的 items
    const { data, error } = await supabase
      .rpc('get_batch_folder_items_recursive', { target_folder_ids: folderIds });
    
    if (error) {
      console.error('批次查詢 items 失敗:', error);
      return { data: {}, error };
    }
    
    // 轉換格式
    const allItems = (data || []).map(item => ({
      id: item.item_id,
      folder_id: item.folder_id,
      folder_path: item.folder_path || [],
      title: item.title,
      description: item.description,
      start_date: item.start_date,
      end_date: item.end_date,
      days: item.days,
      status: item.status,
      completed: item.completed,
      completed_at: item.completed_at,
      priority: item.priority,
      assignee_id: item.assignee_id,
      creator_id: item.creator_id,
      assignee: item.assignee_name ? {
        name: item.assignee_name
      } : null,
      inherited_tags: item.inherited_tags,
      created_at: item.created_at
    }));
    
    // 取得所有涉及的 folder IDs（包含 folder_path 中的所有 folders）
    const allFolderIds = new Set();
    allItems.forEach(item => {
      if (item.folder_id) allFolderIds.add(item.folder_id);
      if (item.folder_path) {
        item.folder_path.forEach(fid => allFolderIds.add(fid));
      }
    });
    
    // 查詢這些 folders 的資訊以便權限判斷
    let foldersMap = {};
    if (allFolderIds.size > 0) {
      const { data: foldersData, error: foldersError } = await supabase
        .from('folders')
        .select('id, parent_id, is_public, department_id, shared_with_departments, owner_id, folder_permissions(user_id, role)')
        .in('id', Array.from(allFolderIds));
      
      if (!foldersError && foldersData) {
        foldersMap = foldersData.reduce((acc, folder) => {
          acc[folder.id] = folder;
          return acc;
        }, {});
      }
    }
    
    // 過濾權限（傳入 folders 資訊）
    const visibleItems = filterVisibleItems(allItems, currentProfile, foldersMap);
    
    // 根據 folder_id 和 path 正確分組任務到對應的根 folder
    const itemsByFolder = {};
    folderIds.forEach(folderId => {
      itemsByFolder[folderId] = [];
    });
    
    visibleItems.forEach(item => {
      // 檢查這個任務屬於哪些根 folder
      folderIds.forEach(rootFolderId => {
        // 如果任務直接在這個根 folder 下
        if (item.folder_id === rootFolderId) {
          itemsByFolder[rootFolderId].push(item);
        }
        // 或者任務的 folder 的 path 包含這個根 folder（表示是子資料夾）
        else if (item.folder_path && item.folder_path.includes(rootFolderId)) {
          itemsByFolder[rootFolderId].push(item);
        }
      });
    });
    
    return { data: itemsByFolder, error: null };
  } catch (error) {
    console.error('Error batch fetching items:', error);
    return { data: null, error };
  }
};

/**
 * 取得 Folder 下的所有 Items
 * @param {number} folderId - 資料夾 ID
 * @param {boolean} includeDescendants - 是否包含子資料夾的任務
 * @param {object} cachedProfile - (選用) 已快取的 profile，避免重複查詢
 */
export const getItemsByFolder = async (folderId, includeDescendants = false, cachedProfile = null) => {
  try {
    // 如果沒有傳入 cachedProfile，使用快取
    let currentProfile = cachedProfile;
    
    if (!currentProfile) {
      const { profile } = await getCachedUserAndProfile(supabase);
      if (!profile) return { data: [], error: new Error('找不到使用者資料') };
      currentProfile = profile;
    }
    
    // 如果需要包含子資料夾，使用遞迴函數
    if (includeDescendants) {
      const { data, error } = await supabase
        .rpc('get_folder_items_recursive', { target_folder_id: folderId });
      
      if (error) throw error;
      
      // 將結果轉換為標準格式（因為 RPC 返回的欄位名稱不同）
      const items = (data || []).map(item => ({
        id: item.item_id,
        folder_id: item.folder_id,
        title: item.title,
        description: item.description,
        start_date: item.start_date,
        end_date: item.end_date,
        days: item.days,
        status: item.status,
        completed: item.completed,
        completed_at: item.completed_at,  // 新增 completed_at 欄位映射
        priority: item.priority,
        assignee_id: item.assignee_id,
        // RPC 已經包含 assignee_name，我們將它轉換為 assignee 物件格式
        assignee: item.assignee_name ? {
          name: item.assignee_name
        } : null,
        inherited_tags: item.inherited_tags,
        created_at: item.created_at
      }));
      
      // 使用 middleware 過濾
      const visibleItems = filterVisibleItems(items, currentProfile);
      
      return { data: visibleItems, error: null };
    }
    
    // 否則只查詢該資料夾本身的任務
    // 由於 assignee_id 的外鍵指向 auth.users 而不是 profiles，
    // 我們需要手動處理關聯
    const { data: itemsData, error: itemsError } = await supabase
      .from('items')
      .select('*')
      .eq('folder_id', folderId)
      .order('display_order', { ascending: true })
      .order('start_date', { ascending: true });

    if (itemsError) throw itemsError;
    
    // 獲取所有不重複的 assignee_id
    const assigneeIds = [...new Set(itemsData
      .map(item => item.assignee_id)
      .filter(id => id !== null))];
    
    // 如果有 assignee_id，批次查詢 profiles（name 已移除，改由 client-side join 取得）
    let profilesMap = {};
    if (assigneeIds.length > 0) {
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, email')
        .in('id', assigneeIds);

      if (!profilesError && profilesData) {
        profilesMap = profilesData.reduce((acc, profile) => {
          acc[profile.id] = profile;
          return acc;
        }, {});
      }
    }
    
    // 將 profiles 資訊附加到每個 item
    const data = itemsData.map(item => ({
      ...item,
      assignee: item.assignee_id ? profilesMap[item.assignee_id] || null : null
    }));

    // 使用 middleware 過濾
    const visibleItems = filterVisibleItems(data, currentProfile);

    if (itemsError) throw itemsError;
    return { data: visibleItems, error: null };
  } catch (error) {
    console.error('Error fetching items:', error);
    return { data: null, error };
  }
};

/**
 * 取得單一 Item
 */
export const getItem = async (itemId) => {
  try {
    const { data, error } = await supabase
      .from('items')
      .select('*')
      .eq('id', itemId)
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching item:', error);
    return { data: null, error };
  }
};

/**
 * 更新 Item
 */
export const updateItem = async (itemId, updates) => {
  try {
    // 先取得更新前的資料
    const { data: beforeData } = await supabase
      .from('items')
      .select('*')
      .eq('id', itemId)
      .single();
    
    const updateData = {};
    
    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.start_date !== undefined) updateData.start_date = updates.start_date;
    if (updates.end_date !== undefined) updateData.end_date = updates.end_date;
    if (updates.days !== undefined) updateData.days = updates.days;
    if (updates.priority !== undefined) updateData.priority = updates.priority;
    if (updates.folder_id !== undefined) updateData.folder_id = updates.folder_id;
    // 空字串轉為 null（未指派負責人時）
    if (updates.assignee_id !== undefined) updateData.assignee_id = updates.assignee_id === '' ? null : updates.assignee_id;
    if (updates.display_order !== undefined) updateData.display_order = updates.display_order;
    
    // 處理 status 變更時的 completed_at 邏輯
    if (updates.status !== undefined) {
      updateData.status = updates.status;
      
      // 如果改成 completed，設定 completed_at 為當下時間
      if (updates.status === 'completed') {
        updateData.completed_at = new Date().toISOString();
        updateData.completed = true;  // 同時設定 completed flag
      } 
      // 如果從 completed 改成其他狀態，清除 completed_at
      else {
        updateData.completed_at = null;
        updateData.completed = false;
      }
    }
    
    // 如果直接更新 completed flag
    if (updates.completed !== undefined && updates.status === undefined) {
      updateData.completed = updates.completed;
      
      if (updates.completed) {
        updateData.completed_at = new Date().toISOString();
        updateData.status = 'completed';
      } else {
        updateData.completed_at = null;
      }
    }

    const { data, error } = await supabase
      .from('items')
      .update(updateData)
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;

    await logAudit('update', 'item', itemId, data.title, {
      before: beforeData,
      after: data
    });

    // 如果是 ticket 底下的 item，更新 ticket activity
    if (beforeData?.ticket_id) {
      const { profile } = await getCachedUserAndProfile(supabase);
      await updateTicketActivity(beforeData.ticket_id, profile?.id);
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error updating item:', error);
    return { data: null, error };
  }
};

/**
 * 移動 Item 到另一個 Folder
 */
export const moveItem = async (itemId, newFolderId) => {
  try {
    const { data, error } = await supabase
      .from('items')
      .update({ folder_id: newFolderId })
      .eq('id', itemId)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error moving item:', error);
    return { data: null, error };
  }
};

/**
 * 刪除 Item
 */
export const deleteItem = async (itemId) => {
  try {
    // 先取得 item 資訊用於日誌
    const { data: itemData } = await supabase
      .from('items')
      .select('title, company_id, department_id, ticket_id')
      .eq('id', itemId)
      .single();

    const { error } = await supabase
      .from('items')
      .delete()
      .eq('id', itemId);

    if (error) throw error;

    await logAudit('delete', 'item', itemId, itemData?.title, {
      before: itemData
    });

    if (itemData?.ticket_id) {
      const { profile } = await getCachedUserAndProfile(supabase);
      await updateTicketActivity(itemData.ticket_id, profile?.id);
    }

    return { error: null };
  } catch (error) {
    console.error('Error deleting item:', error);
    return { error };
  }
};

/**
 * 批次更新 Items 狀態
 */
export const batchUpdateItemStatus = async (itemIds, status) => {
  try {
    const updateData = { status };
    
    // 處理 completed_at 邏輯
    if (status === 'completed') {
      updateData.completed_at = new Date().toISOString();
      updateData.completed = true;
    } else {
      updateData.completed_at = null;
      updateData.completed = false;
    }
    
    const { data, error } = await supabase
      .from('items')
      .update(updateData)
      .in('id', itemIds)
      .select();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error batch updating items:', error);
    return { data: null, error };
  }
};

/**
 * 批次移動 Items
 */
export const batchMoveItems = async (itemIds, newFolderId) => {
  try {
    const { data, error } = await supabase
      .from('items')
      .update({ folder_id: newFolderId })
      .in('id', itemIds)
      .select();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error batch moving items:', error);
    return { data: null, error };
  }
};

/**
 * 根據篩選條件取得 Items
 */
/**
 * 取得符合條件的所有 Items（支援多種篩選條件）
 * @param {Object} filters - 篩選條件
 * @param {number|null} departmentId - 部門 ID（用於過濾）
 * @param {string|null} viewingCompanyId - 公司 ID（用於過濾）
 */
export const getFilteredItems = async (filters = {}, departmentId = null, viewingCompanyId = null) => {
  try {
    // 使用快取避免重複查詢
    const { profile: currentProfile } = await getCachedUserAndProfile(supabase);
    if (!currentProfile) return { data: [], error: new Error('找不到使用者資料') };
    
    // 🚀 優化：只選取需要的欄位，並根據 department_id 過濾（API 層安全）
    let query = supabase
      .from('items')
      .select(`
        id,
        title,
        description,
        folder_id,
        status,
        priority,
        assignee_id,
        creator_id,
        start_date,
        end_date,
        completed,
        completed_at,
        inherited_tags,
        created_at,
        updated_at,
        department_id,
        company_id
      `);

    // 🔒 API 層安全過濾
    if (currentProfile.role === 'super_admin') {
      // Super Admin: 優先用部門過濾，沒選部門則用公司過濾
      if (departmentId) {
        query = query.eq('department_id', departmentId);
      } else if (viewingCompanyId) {
        query = query.eq('company_id', viewingCompanyId);
      } else {
        return { data: [], error: null };
      }
    } else if (currentProfile.role === 'company_admin') {
      // Company Admin: 只看本公司
      if (!currentProfile.company_id) {
        console.error('❌ Company Admin 缺少 company_id');
        return { data: [], error: new Error('缺少公司資訊') };
      }
      query = query.eq('company_id', currentProfile.company_id);
    } else {
      // Department Admin / Member: 只看本部門
      if (!currentProfile.department_id) {
        console.error('❌ 使用者缺少 department_id');
        return { data: [], error: new Error('缺少部門資訊') };
      }
      query = query.eq('department_id', currentProfile.department_id);
    }

    // 狀態篩選
    if (filters.status && filters.status.length > 0) {
      query = query.in('status', filters.status);
    }

    // 負責人篩選
    if (filters.assigneeId) {
      query = query.eq('assignee_id', filters.assigneeId);
    }

    // 日期範圍篩選
    if (filters.startDate) {
      query = query.gte('start_date', filters.startDate);
    }
    if (filters.endDate) {
      query = query.lte('start_date', filters.endDate);
    }

    // 優先級篩選
    if (filters.priority !== undefined) {
      query = query.eq('priority', filters.priority);
    }

    // 完成狀態篩選
    if (filters.completed !== undefined) {
      query = query.eq('completed', filters.completed);
    }

    // 類別標籤篩選（使用 JSONB 查詢）
    if (filters.tags && Object.keys(filters.tags).length > 0) {
      Object.entries(filters.tags).forEach(([category, value]) => {
        query = query.contains('inherited_tags', { [category]: value });
      });
    }

    // 🚀 不需要額外過濾，RLS 會自動處理權限
    query = query.order('start_date', { ascending: true });

    const { data: itemsData, error: itemsError } = await query;

    if (itemsError) throw itemsError;
    
    // 🚀 補充 assignee 資訊（從 DataContext 的 profiles，不額外查詢）
    // 如果 DataContext 尚未載入，才批次查詢
    const assigneeIds = [...new Set(itemsData
      ?.map(item => item.assignee_id)
      .filter(id => id !== null))] || [];
    
    let assigneesMap = {};
    if (assigneeIds.length > 0) {
      const { data: assignees } = await supabase
        .from('profiles')
        .select('id, email')
        .in('id', assigneeIds);

      if (assignees) {
        assigneesMap = assignees.reduce((acc, profile) => {
          acc[profile.id] = profile;
          return acc;
        }, {});
      }
    }
    
    // 補充 assignee 資訊
    const itemsWithAssignees = (itemsData || []).map(item => ({
      ...item,
      assignee: item.assignee_id ? assigneesMap[item.assignee_id] || null : null
    }));
    
    return { data: itemsWithAssignees, error: null };
  } catch (error) {
    console.error('Error fetching filtered items:', error);
    return { data: null, error };
  }
};

/**
 * 取得所有 Items（用於篩選器選項生成）
 */
export const getAllItems = async () => {
  try {
    const { data, error } = await supabase
      .from('items')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching all items:', error);
    return { data: null, error };
  }
};

/**
 * 批次取得多個資料夾的任務數量
 * @param {Array<number>} folderIds - 資料夾 ID 陣列
 * @returns {Object} { data: { folderId: { total, incomplete } }, error }
 */
export const getBatchItemCounts = async (folderIds) => {
  try {
    if (!folderIds || folderIds.length === 0) {
      return { data: {}, error: null };
    }

    // 一次查詢所有資料夾的任務
    const { data: items, error } = await supabase
      .from('items')
      .select('folder_id, completed')
      .in('folder_id', folderIds);

    if (error) throw error;

    // 在前端計算每個資料夾的任務數量
    const counts = {};
    
    // 初始化所有資料夾的計數
    folderIds.forEach(id => {
      counts[id] = { total: 0, incomplete: 0 };
    });
    
    // 統計任務數量
    (items || []).forEach(item => {
      if (!counts[item.folder_id]) {
        counts[item.folder_id] = { total: 0, incomplete: 0 };
      }
      counts[item.folder_id].total += 1;
      if (!item.completed) {
        counts[item.folder_id].incomplete += 1;
      }
    });

    return { data: counts, error: null };
  } catch (error) {
    console.error('Error fetching batch item counts:', error);
    return { data: null, error };
  }
};

